"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAppCopy } from "@/content/app-copy";
import { Locale } from "@/i18n-config";
import { toSupportedLocale } from "@/lib/email-locale";
import { createClient } from "@/lib/supabase/client";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordView({ lang }: { lang: Locale }) {
  const normalizedLang = toSupportedLocale(lang);
  const copy = getAppCopy(normalizedLang).auth.forgotPassword;
  const supabase = useMemo(() => createClient(), []);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedEmail = email.trim().toLowerCase();
    setError(null);

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError(copy.invalidEmail);
      return;
    }

    setIsSubmitting(true);

    const redirectTo = `${window.location.origin}/${normalizedLang}/reset-password`;
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo,
    });

    if (resetError) {
      setError(copy.genericError);
      setIsSubmitting(false);
      return;
    }

    setIsEmailSent(true);
    setIsSubmitting(false);
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#f4efe3] px-4 pb-12 pt-24 md:pb-16 md:pt-28">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_12%,rgba(180,83,9,0.14),transparent_36%),radial-gradient(circle_at_84%_16%,rgba(15,118,110,0.14),transparent_36%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(29,38,34,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(29,38,34,0.05)_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_88%_72%_at_50%_0%,#000_72%,transparent_100%)] opacity-25" />

      <div className="container relative flex min-h-[calc(100vh-9rem)] items-center justify-center">
        <div className="w-full max-w-md rounded-3xl border border-[#d8cab2] bg-[#fffaf1] p-8 shadow-[0_12px_28px_rgba(34,47,42,0.08)]">
          {isEmailSent ? (
            <>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0f766e]/25 bg-[#eaf8f5] px-3 py-1 text-sm font-medium text-[#1c4d45]">
                <CheckCircle2 size={16} />
                {copy.successTitle}
              </div>
              <p className="mt-4 text-[#23423c]">{copy.successMessage}</p>

              <Link href={`/${normalizedLang}/login`} className="mt-8 block">
                <Button className="w-full border border-[#0f766e]/30 bg-[#0f766e] hover:bg-[#0e655e]">
                  {copy.backToLogin}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold text-[#1b2220] md:text-4xl">{copy.title}</h1>
                <p className="mt-3 text-[#3e5350]">{copy.subtitle}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {error ? (
                  <div className="rounded-xl border border-[#c24f45]/25 bg-[#fff1ef] p-3 text-sm text-[#8b2f28]">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} />
                      <span>{error}</span>
                    </div>
                  </div>
                ) : null}

                <Input
                  id="email"
                  type="email"
                  label={copy.emailLabel}
                  placeholder={copy.emailPlaceholder}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={isSubmitting}
                  className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
                />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full border border-[#0f766e]/30 bg-[#0f766e] transition-transform duration-200 hover:bg-[#0e655e]"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {copy.submitting}
                    </>
                  ) : (
                    copy.submit
                  )}
                </Button>

                <div className="text-center text-sm">
                  <Link href={`/${normalizedLang}/login`} className="font-medium text-[#0f766e] hover:text-[#0d5d57]">
                    {copy.backToLogin}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
